import { ColumnDef } from "@tanstack/react-table";
import moment from "moment/moment";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { FaCheckCircle } from "react-icons/fa";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { LuTrash2 } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { AvatarPlaceholder } from "../../components/avatar/AvatarPlaceholder";
import { AvatarStack } from "../../components/avatar/AvatarStack";
import { Badge } from "../../components/badge/Badge";
import { SettingsIconButton } from "../../components/button/SettingsIconButton";
import { Card } from "../../components/card/Card";
import { StatsCardsGrid } from "../../components/card/StatsCardsGrid";
import { SettingsDropdown } from "../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { SettingsConfirmModal } from "../../components/modal/SettingsConfirmModal";
import { ListPager } from "../../components/pagination/ListPager";
import { DotsSvg } from "../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../components/svg/TrashSvg";
import { Table } from "../../components/table/Table";
import {
  getInitials,
  getInitialsFromFullName,
  timeFormatter,
} from "../../constants/constants";
import { useAppDispatch } from "../../redux/redux";
import { deleteTime } from "../../services/AppService";
import { ITime } from "../../types/types";
import { navigateToContact } from "../contacts/utils/navigateToContacts";
import EditTime from "./EditTime";
import { Time } from "./InvoiceTime";
import {
  DEFAULT_VISIBLE_TIME_FILTERS,
  TimeFilterKey,
  TimeFilters,
} from "./TimeFilters";
import {
  DEFAULT_VISIBLE_TIME_COLUMNS,
  getTimeListHeaderItems,
  TimeColumnKey,
  TimeListHeaderItem,
} from "./types/time-list";

const EMPTY_VALUE = "-";

export type TimeStatsData = {
  billedAmount: number;
  billedHours: number;
  unBilledHours: number;
};

type Props = {
  buttonTimerVariant?: () => JSX.Element;
  getData: () => Promise<void> | void;
  filterTime: ITime[] | undefined;
  billableHours?: TimeStatsData;
  filter: Time;
  setFilter: Dispatch<SetStateAction<Time>>;
  visibleFilters?: TimeFilterKey[];
  visibleColumns?: TimeColumnKey[];
  presetFilters?: Partial<Time>;
  showSummaryRow?: boolean;
  isLoading?: boolean;
  page?: number;
  totalPages?: number;
  /** Total entries and page size, for "Showing 21–40 of 75". */
  total?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
};

const formatCurrency = (value?: number | null) =>
  `$${(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const TimesListView: FC<Props> = ({
  buttonTimerVariant,
  getData,
  filterTime,
  billableHours,
  filter,
  setFilter,
  visibleFilters = DEFAULT_VISIBLE_TIME_FILTERS,
  visibleColumns = DEFAULT_VISIBLE_TIME_COLUMNS,
  presetFilters,
  showSummaryRow = true,
  isLoading = false,
  page = 1,
  totalPages = 1,
  total,
  pageSize,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [currentTime, setCurrentTime] = useState<ITime | null>(null);
  const [timeToDelete, setTimeToDelete] = useState<ITime | null>(null);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [collapsedItems, setCollapsedItems] = useState<string[]>([]);
  // QA batch 15: a deleted entry leaves the list at once (before the refresh).
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const totalRate = useCallback((value: string) => {
    const parts = value.split("h");
    const hours = Number(parts[0] || 0);
    const minutes = Number(parts[1]?.replace("m", "").trim() || 0) / 60;

    return hours + minutes;
  }, []);

  // Non-billable time has no amount (QA batch 8; same as the reports).
  const getTimeTotalAmount = useCallback(
    (time: ITime) =>
      time.isBillable === false
        ? 0
        : Math.trunc(
            (time.assignedUser?.hourlyRate || 0) *
              totalRate(timeFormatter(time.duration || 0)),
          ),
    [totalRate],
  );

  const onClickSort = useCallback(
    (item: TimeListHeaderItem) => {
      if (item.hideSort) {
        return;
      }

      setFilter((prev) => ({
        ...prev,
        sortBy: item.sort,
        sortOrder:
          item.sort === prev.sortBy
            ? prev.sortOrder === "asc"
              ? "desc"
              : "asc"
            : "asc",
      }));
    },
    [setFilter],
  );

  const sortedData = useMemo(() => {
    if (!filterTime) {
      return [];
    }
    const visible = deletedIds.length
      ? filterTime.filter((item) => !deletedIds.includes(item.id))
      : filterTime;

    if (filter.sortBy === "Total") {
      return [...visible].sort((a, b) => {
        const totalA = getTimeTotalAmount(a);
        const totalB = getTimeTotalAmount(b);

        return filter.sortOrder === "asc" ? totalA - totalB : totalB - totalA;
      });
    }

    return visible;
  }, [
    filterTime,
    deletedIds,
    filter.sortBy,
    filter.sortOrder,
    getTimeTotalAmount,
  ]);

  const headerItems = useMemo(
    () => getTimeListHeaderItems({ visibleColumns }),
    [visibleColumns],
  );

  const headerItemByColumn = useMemo(() => {
    return headerItems.reduce<
      Partial<Record<TimeColumnKey, TimeListHeaderItem>>
    >((acc, headerItem) => {
      return {
        ...acc,
        [headerItem.column]: headerItem,
      };
    }, {});
  }, [headerItems]);

  const renderHeader = useCallback(
    (column: TimeColumnKey) => {
      const item = headerItemByColumn[column];

      if (!item) {
        return null;
      }

      return (
        <div
          className={`flex items-center gap-2 ${
            !item.hideSort ? "cursor-pointer" : ""
          }`}
          onClick={() => onClickSort(item)}>
          <div className={"whitespace-nowrap"}>{item.title}</div>

          {!item.hideSort &&
            filter.sortBy === item.sort &&
            (filter.sortOrder === "asc" ? (
              <HiChevronUp size={24} />
            ) : (
              <HiChevronDown size={24} />
            ))}
        </div>
      );
    },
    [filter.sortBy, filter.sortOrder, headerItemByColumn, onClickSort],
  );

  const handleOpenEdit = useCallback((time: ITime) => {
    setCurrentTime(time);
    setIsOpenEdit(true);
  }, []);

  const handleOpenDelete = useCallback((time: ITime) => {
    setTimeToDelete(time);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!timeToDelete) {
      return;
    }

    setIsLoadingDelete(true);
    const result = await dispatch(deleteTime(timeToDelete));
    if (deleteTime.fulfilled.match(result)) {
      setDeletedIds((ids) => [...ids, timeToDelete.id]);
    }
    await getData();
    setIsLoadingDelete(false);
    setTimeToDelete(null);
  }, [dispatch, getData, timeToDelete]);

  const handleToggleNotes = useCallback((timeId: string) => {
    setCollapsedItems((prev) =>
      prev.includes(timeId)
        ? prev.filter((item) => item !== timeId)
        : [...prev, timeId],
    );
  }, []);

  const columns = useMemo<ColumnDef<ITime, unknown>[]>(() => {
    const columnsMap: Record<TimeColumnKey, ColumnDef<ITime, unknown>> = {
      [TimeColumnKey.Date]: {
        id: TimeColumnKey.Date,
        header: () => renderHeader(TimeColumnKey.Date),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.date
            ? moment(row.original.date).format("MMM DD, YYYY")
            : EMPTY_VALUE,
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className:
            "min-w-[120px] items-center px-[16px] py-[8px] text-[14px] font-semibold",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Type]: {
        id: TimeColumnKey.Type,
        header: () => renderHeader(TimeColumnKey.Type),
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className={"flex items-center"}>
              <Badge
                label={row.original.jobTaskId ? "Subtask" : "Work"}
                variant={"filled"}
                color={row.original.jobTaskId ? "success" : "secondary"}
                size={"md"}
              />
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "items-center px-4 py-2",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.WorkName]: {
        id: TimeColumnKey.WorkName,
        header: () => renderHeader(TimeColumnKey.WorkName),
        enableSorting: false,
        cell: ({ row }) => {
          const time = row.original;

          const isWorkTask = !!time.jobTaskId;

          const taskName = isWorkTask ? time.workTask?.name : time.work?.name;

          return (
            <div className={"flex items-center"}>
              <div className={"max-w-[300px] items-center whitespace-normal"}>
                <div
                  onClick={() => {
                    if (time.work?.id) {
                      navigate(`/work/${time.work.id}`);
                    }
                  }}
                  className={
                    "w-fit max-w-full cursor-pointer truncate text-sm font-semibold hover:text-[#7C66F0] hover:underline"
                  }>
                  {taskName || EMPTY_VALUE}
                </div>
              </div>
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "items-center px-4 py-2",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Contact]: {
        id: TimeColumnKey.Contact,
        header: () => renderHeader(TimeColumnKey.Contact),
        enableSorting: false,
        cell: ({ row }) => {
          const time = row.original;

          const contactName = time.contactId
            ? time.contact
              ? `${time.contact.firstName || ""} ${
                  time.contact.lastName || ""
                }`.trim()
              : "Contact name"
            : time.businessContact?.companyName || "Contact name";

          return (
            <div
              onClick={(event) => {
                event.stopPropagation();

                const type = time?.contactId ? "contact" : "organization";
                const customerId =
                  time?.customerId || time?.work?.customerId || "";

                if (type === "contact" && !customerId) {
                  toast.error("Contact not found");
                  return;
                }

                const link = navigateToContact({
                  type: type,
                  customerId: customerId,
                  id: time?.businessContactId || "",
                });

                navigate(link);
              }}
              className={
                "flex w-fit max-w-[200px] cursor-pointer items-center gap-2 truncate"
              }>
              <AvatarPlaceholder
                size={"2xs"}
                label={
                  time.contactId
                    ? getInitials(
                        time.contact?.firstName || "",
                        time.contact?.lastName || "",
                      )
                    : getInitialsFromFullName(
                        time.businessContact?.companyName || "",
                      )
                }
                type={time.contactId ? "blue" : "purple"}
              />

              <div
                className={
                  "font-semibold hover:text-[#7C66F0] hover:underline"
                }>
                {contactName}
              </div>
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "items-center px-4 py-2",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.WorkType]: {
        id: TimeColumnKey.WorkType,
        header: () => renderHeader(TimeColumnKey.WorkType),
        enableSorting: false,
        cell: ({ row }) => row.original.work?.service?.name ?? EMPTY_VALUE,
        footer: () => (
          <div className={"text-end text-sm font-bold text-gray-800"}>
            Total:
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "items-center px-4 py-2 text-sm font-semibold",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.TotalTime]: {
        id: TimeColumnKey.TotalTime,
        header: () => renderHeader(TimeColumnKey.TotalTime),
        enableSorting: false,
        cell: ({ row }) => {
          const time = row.original;

          return (
            <div className={"text-sm font-semibold"}>
              {timeFormatter(time.duration || 0)}
            </div>
          );
        },
        footer: () => (
          <div className={"whitespace-nowrap text-sm font-bold text-gray-800"}>
            {timeFormatter(sortedData?.[0]?.totalDuration || 0)}
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "items-center px-[16px] py-[8px]",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Total]: {
        id: TimeColumnKey.Total,
        header: () => renderHeader(TimeColumnKey.Total),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isBillable === false ? (
            <span
              className={"whitespace-nowrap font-medium text-gray-500"}
              title={"Non-billable time has no amount"}>
              Non-billable
            </span>
          ) : (
            formatCurrency(getTimeTotalAmount(row.original))
          ),
        footer: () => (
          <div
            className={"whitespace-nowrap text-sm font-bold text-gray-800"}
            title={"Billable time only"}>
            {formatCurrency(sortedData?.[0]?.totalAmount)}
          </div>
        ),
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "px-[16px] py-[8px] text-[14px] font-semibold",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Status]: {
        id: TimeColumnKey.Status,
        header: () => renderHeader(TimeColumnKey.Status),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.isBilled ? (
            <div className={"flex items-center gap-2"}>
              <FaCheckCircle className={"text-green-500"} /> Billed
            </div>
          ) : row.original.isBillable === false ? (
            <span className={"font-medium text-gray-500"}>Non-billable</span>
          ) : (
            "Unbilled"
          ),
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "px-[16px] py-[8px] text-[14px] font-semibold",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Assignee]: {
        id: TimeColumnKey.Assignee,
        header: () => renderHeader(TimeColumnKey.Assignee),
        enableSorting: false,
        cell: ({ row }) => {
          const userProfile = row.original.assignedUser?.userProfile;
          const firstName = userProfile?.firstName || "";
          const lastName = userProfile?.lastName || "";
          const fullName = `${firstName} ${lastName}`.trim();

          return (
            <div className={"flex items-center text-sm"}>
              <AvatarStack
                previewTitle={"Users:"}
                withPreview
                items={[
                  {
                    id: userProfile?.userId,
                    label: getInitials(firstName, lastName),
                    name: fullName,
                    color: userProfile?.color,
                  },
                ]}
              />
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className: "relative bg-transparent px-4 py-2",
          footerClassName: "px-4 py-2",
        },
      },

      [TimeColumnKey.Notes]: {
        id: TimeColumnKey.Notes,
        header: () => renderHeader(TimeColumnKey.Notes),
        enableSorting: false,
        cell: ({ row }) => {
          const time = row.original;
          const isExpanded = collapsedItems.includes(time.id);

          return (
            <div
              onClick={() => handleToggleNotes(time.id)}
              className={
                isExpanded
                  ? "max-w-[220px] whitespace-pre-wrap break-words"
                  : "max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap"
              }>
              {time.notes || EMPTY_VALUE}
            </div>
          );
        },
        meta: {
          headerClassName: "p-4 text-start text-sm text-gray-500",
          className:
            "w-[220px] max-w-[220px] px-[16px] py-[8px] text-[14px] align-top",
          footerClassName: "px-4 py-2",
        },
      },
    };

    const visibleColumnsList = headerItems
      .map((item) => columnsMap[item.column])
      .filter((column): column is ColumnDef<ITime, unknown> => Boolean(column));

    return [
      ...visibleColumnsList,
      {
        id: "actions",
        header: () => null,
        enableSorting: false,
        cell: ({ row }) => {
          const time = row.original;

          return (
            <div
              className={"flex justify-end"}
              onClick={(event) => event.stopPropagation()}>
              <SettingsDropdown
                actionsList={[
                  {
                    name: "Edit",
                    action: () => handleOpenEdit(time),
                    icon: <EditPencilSvg />,
                  },
                  {
                    name: "Delete",
                    action: () => handleOpenDelete(time),
                    icon: <TrashSvg />,
                    extraClasses: "text-[#DC2626]",
                    iconColor: "text-[#DC2626]",
                  },
                ]}>
                <SettingsIconButton
                  icon={<DotsSvg />}
                  onClick={() => null}
                  size={"small"}
                />
              </SettingsDropdown>
            </div>
          );
        },
        footer: () => null,
        meta: {
          headerClassName: "w-[48px] px-4 py-2",
          className: "w-[48px] px-4 py-2",
          footerClassName: "w-[48px] px-4 py-2",
        },
      },
    ];
  }, [
    collapsedItems,
    getTimeTotalAmount,
    handleOpenDelete,
    handleOpenEdit,
    handleToggleNotes,
    navigate,
    renderHeader,
    sortedData,
    headerItems,
  ]);

  return (
    <>
      {/* min-w-0: the table scrolls inside its card instead of pushing the
          page wider than the screen (cut off at 1440px, QA batch 8). */}
      <div
        className={
          "grid h-full w-full min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[max-content_1fr] gap-6"
        }>
        {billableHours && (
          <StatsCardsGrid
            items={[
              {
                id: "billable-amount",
                label: "Billable amount",
                value: `$${billableHours.billedAmount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`,
              },
              {
                id: "billed-hours",
                label: "Billed hours",
                value: timeFormatter(billableHours.billedHours),
              },
              {
                id: "unbilled-hours",
                label: "Unbilled hours",
                value: timeFormatter(billableHours.unBilledHours),
              },
            ]}
          />
        )}

        <Card extraClasses={"grid min-w-0 grid-rows-1 overflow-visible"}>
          <div
            className={
              "grid h-full w-full min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[max-content_1fr] gap-4"
            }>
            <div
              className={
                "grid grid-cols-[1fr_max-content] gap-2 px-8 pt-6 max-md:grid-cols-1 max-md:px-4"
              }>
              <TimeFilters
                filter={filter}
                setFilter={setFilter}
                visibleFilters={visibleFilters}
                presetFilters={presetFilters}
              />

              {buttonTimerVariant && <div>{buttonTimerVariant()}</div>}
            </div>

            {isLoading ? (
              <LottieLoading />
            ) : (
              <>
                <div
                  className={
                    "max-h-full w-full overflow-x-auto px-6 max-md:px-2"
                  }>
                  <Table
                    columns={columns}
                    data={sortedData}
                    getRowId={(time) => time.id}
                    theadClassName={"border-b border-gray-300"}
                    tbodyClassName={"text-sm"}
                    rowClassName={
                      "h-[49px] border-b-[1px] border-gray-300 last-of-type:border-none hover:bg-gray-100"
                    }
                    showFooter={showSummaryRow && sortedData.length > 0}
                    footerRowClassName={"sticky bottom-0 left-0 bg-gray-100"}
                  />
                </div>

                {onPageChange && sortedData.length > 0 && (
                  <ListPager
                    page={page}
                    totalPages={totalPages}
                    total={total}
                    pageSize={pageSize}
                    onChange={onPageChange}
                  />
                )}
              </>
            )}
          </div>
        </Card>
      </div>

      {isOpenEdit && currentTime && (
        <EditTime
          closeModal={() => setIsOpenEdit(false)}
          time={currentTime}
          onSuccess={getData}
          onDeleted={(id) => setDeletedIds((ids) => [...ids, id])}
        />
      )}

      {timeToDelete && (
        <SettingsConfirmModal
          onClose={() => setTimeToDelete(null)}
          onConfirm={handleDeleteConfirm}
          icon={<LuTrash2 size={24} />}
          title={"Delete this time entry?"}
          description={"Do you want to permanently delete this time entry?"}
          confirmText={"Delete"}
          isLoading={isLoadingDelete}
        />
      )}
    </>
  );
};
